import React, { useEffect, useState } from 'react';
import { Briefcase } from '@phosphor-icons/react';
import { createBrowserSearchClient } from '../../../utils/search/create-browser-search-client';
import type { AlgoliaCompanyRecord } from '../../../types/algolia-company-record';
import { EmployerScoreCard } from '../../../components/EmployerScoreCard';
import { CompanyCommunityInsights } from '../../../components/company-profile';
import { CompanyLogo } from '../../../components/CompanyLogo';
// Employer analytics charts (shared with job detail company section — single implementation).
import {
  MonthlyHiringChart,
  StarburstChart,
  MoneyGraph,
} from '../../job/@jobId/@seoSlug/components/CompanyCharts';
import { getIndexName, getJobsIndexNamePostedDateDesc } from '../../../constants';
import { buildCompanyOpenRolesAlgoliaParams } from '../../../utils/company/build-company-open-roles-algolia-params';
import {
  algoliaJobHitHasStableObjectId,
  normalisedAlgoliaJobObjectId,
} from '../../../utils/company/normalised-algolia-job-object-id';
import { generateJobUrl, toRelativePath, type Locale } from '../../../utils/routing/generate-url';
import { BASE_URL, LOCALIZED_PATHS } from '../../../utils/constants/url-constants';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import { toSafeIntlStringValue } from '../../../utils/intl/to-safe-intl-string-value';
import { buildCompanyProfileDataTables } from '../../../utils/company/build-company-profile-data-tables';
import { buildCompanySeoNarrative } from '../../../utils/company/build-company-seo-narrative';
import { getCompanyProfileUiCopy, type CompanyProfileUiCopy } from '../../../utils/company/company-profile-ui-copy';
import { CompanyProfileSeoTables } from '../../../components/company-profile/CompanyProfileSeoTables';
import { BreadcrumbNavigation } from '../../../components/BreadcrumbNavigation';
import type { CompanyProfileLocale } from '../../../utils/seo/company-profile-alternate-urls';
import { usePageContext } from 'vike-react/usePageContext';
import { useCompanyProfilePageData } from '../../../hooks/useCompanyProfilePageData';
import { resolveCompanyProfileRouteSlug } from '../../../utils/company/resolve-company-profile-route-slug';
import { EngagementStrip } from '../../../components/engagement/EngagementStrip';
import { filterCompanyAboutForPageLocale } from '../../../utils/text/is-likely-finnish-prose';
import { resolveRegistryMainBusinessLineForPageLocale } from '../../../utils/company/resolve-registry-main-business-line-for-page-locale';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../../utils/routing/job-url-input';
import { trackEvent, trackJobClick } from '../../../utils/analytics/analytics';

function jobsListingPathForLocale(locale: string): string {
  const l = locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en';
  const job = l === 'fi' ? 'tyopaikat' : l === 'sv' ? 'lediga-jobb' : 'jobs';
  return `/${l}/${job}`;
}

/** Public employer profile — matches resource hub / JobCrawls earth-tone palette */
const shell = {
  page: 'min-h-screen bg-[#F8F5F0] dark:bg-[#170F07]',
  card: 'rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-3 sm:p-4',
  cardHero: 'rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-6',
  h1: 'text-2xl font-bold text-[#1E1A14] dark:text-[#F2EADF]',
  h2: 'text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]',
  body: 'text-sm text-[#3D3529] dark:text-[#C4A882]',
  muted: 'text-sm text-[#7A6E5F] dark:text-[#B8A48C]',
  link: 'text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC]',
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

interface AlgoliaJobHit {
  objectID: string;
  title?: string;
  normalisedTitle?: string;
  normalisedRoleLevel?: string;
  remoteness?: string;
  location?: string[];
  postedDate?: string;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  companyId?: string;
  url?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSalary(min?: number, max?: number): string | null {
  if (!min && !max) return null;
  const fmt = (n: number) => `€${Math.round(n / 100) * 100}`.replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  if (min && max) {
    if (min === max) return `${fmt(min)}/mo`;
    return `${fmt(min)} – ${fmt(max)}/mo`;
  }
  if (min) return `from ${fmt(min)}/mo`;
  if (max) return `up to ${fmt(max)}/mo`;
  return null;
}

function hiringTrendLabel(
  trend: string | undefined,
  copy: ReturnType<typeof getCompanyProfileUiCopy>,
): { label: string; colour: string } | null {
  if (trend === 'growing') {
    return {
      label: copy.trendGrowing,
      colour: 'bg-[#C8DDCE] text-[#3B5E45] dark:bg-[#1A2E22] dark:text-[#98CFA8]',
    };
  }
  if (trend === 'declining') {
    return {
      label: copy.trendDeclining,
      colour: 'bg-[#F5EDD0] text-[#6E4F00] dark:bg-[#2A1E08] dark:text-[#D4A820]',
    };
  }
  if (trend === 'stable') {
    return {
      label: copy.trendStable,
      colour: 'bg-[#F2EADF] text-[#5A4A3A] dark:bg-[#2A1D0F] dark:text-[#C4A882]',
    };
  }
  // No hiringTrend yet (company not scored) — nothing accurate to show.
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SocialLinks({ company }: { company: AlgoliaCompanyRecord }) {
  const links = [
    { href: company.linkedinUrl, label: 'LinkedIn' },
    { href: company.youtubeUrl, label: 'YouTube' },
    { href: company.facebookUrl, label: 'Facebook' },
    { href: company.instagramUrl, label: 'Instagram' },
    { href: company.tiktokUrl, label: 'TikTok' },
  ].filter(l => l.href);

  if (links.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {links.map(l => (
        <a
          key={l.label}
          href={l.href!}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-full border border-[#DDD5C8] bg-[#F8F5F0] text-[#3D3529] transition-colors hover:border-[#3B5E45]/40 hover:text-[#3B5E45] dark:border-[#4A3520] dark:bg-[#170F07] dark:text-[#C4A882] dark:hover:border-[#98CFA8]/40 dark:hover:text-[#98CFA8]"
        >
          {l.label}
        </a>
      ))}
    </div>
  );
}

function SalaryBand({
  company,
  copy,
}: {
  company: AlgoliaCompanyRecord;
  copy: ReturnType<typeof getCompanyProfileUiCopy>;
}) {
  const { medianSalaryMin, medianSalaryMax, salaryPercentile25, salaryPercentile75, salarySampleSize } = company;
  if (!medianSalaryMin && !medianSalaryMax) return null;

  const median = formatSalary(medianSalaryMin, medianSalaryMax);
  const band = formatSalary(salaryPercentile25, salaryPercentile75);

  return (
    <div className={shell.card}>
      <h2 className={`${shell.h2} mb-2`}>{copy.salaryDataHeading}</h2>
      <div className="space-y-1.5">
        {median && (
          <p className={shell.body}>
            <span className="font-medium text-[#1E1A14] dark:text-[#F2EADF]">{copy.medianLabel}:</span> {median}
          </p>
        )}
        {band && (
          <p className={shell.body}>
            <span className="font-medium text-[#1E1A14] dark:text-[#F2EADF]">{copy.middle50Label}:</span> {band}
          </p>
        )}
        {salarySampleSize != null && (
          <p className="text-xs text-[#7A6E5F] dark:text-[#B8A48C]">
            {copy.basedOnListings} {salarySampleSize}{' '}
            {salarySampleSize !== 1 ? copy.jobListingPlural : copy.jobListingSingular}
          </p>
        )}
      </div>
    </div>
  );
}

/** Open roles list — same row chrome as job detail `SimilarJobsSection` (jc-* tokens, logo, salary chip). */
function CompanyOpenRolesPanel({
  companyId,
  locale,
  seeAllHref,
  copy,
}: {
  companyId: string;
  locale: string;
  seeAllHref: string;
  copy: Pick<CompanyProfileUiCopy, 'openRolesHeading' | 'openRolesSub' | 'seeAllJobs' | 'noOpenRoles'>;
}) {
  const [jobs, setJobs] = useState<AlgoliaJobHit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Routes through the search-provider abstraction: Typesense when the
    // search_provider flag (query/cookie/localStorage/env) selects it, else Algolia.
    const client = createBrowserSearchClient();
    if (!client) {
      setLoading(false);
      return;
    }

    let params: ReturnType<typeof buildCompanyOpenRolesAlgoliaParams>;
    try {
      params = buildCompanyOpenRolesAlgoliaParams({ companyId, hitsPerPage: 12 });
    } catch {
      setLoading(false);
      return;
    }

    const indexName = getJobsIndexNamePostedDateDesc(
      ['en', 'fi', 'sv'].includes(locale) ? locale : 'en',
    );

    client
      .search([
        {
          indexName,
          params,
        },
      ])
      .then((res) => {
        const first = res?.results?.[0] as { hits?: AlgoliaJobHit[] } | undefined;
        setJobs((first?.hits ?? []) as AlgoliaJobHit[]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId, locale]);

  if (loading) {
    return (
      <section className="mt-0" aria-labelledby="company-open-roles-heading">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2
              id="company-open-roles-heading"
              className="text-lg font-semibold tracking-tight text-jc-ink"
            >
              {copy.openRolesHeading}
            </h2>
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-jc-accent/20 bg-jc-accent-muted dark:border-jc-accent/35 dark:bg-[#1A2E22]"
              aria-hidden
            >
              <Briefcase className="h-4 w-4 text-jc-cta dark:text-jc-accent" strokeWidth={2} />
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-[52px] animate-pulse rounded-xl border border-jc-border bg-jc-elevated/40 dark:bg-jc-elevated/30"
            />
          ))}
        </div>
      </section>
    );
  }
  const jobLinkLocale = (['en', 'fi', 'sv'].includes(locale) ? locale : 'en') as Locale;
  const origin = typeof window !== 'undefined' ? window.location.origin : BASE_URL;

  const jobsWithIds = jobs.filter((job) => algoliaJobHitHasStableObjectId(job));

  const listItems = jobsWithIds.flatMap((job) => {
    const salary = formatSalary(job.salaryRangeMin, job.salaryRangeMax);
    const oid = normalisedAlgoliaJobObjectId(job.objectID);
    let jobHref: string;
    try {
      jobHref = toRelativePath(
        generateJobUrl({
          baseUrl: origin,
          jobId: oid,
          locale: jobLinkLocale,
          company: resolveJobUrlCompany(job, companyId),
          title: resolveJobUrlTitle(job),
          location: resolveJobUrlLocation(job),
          remoteness: resolveJobUrlRemoteness(job),
        }),
      );
    } catch {
      return [];
    }
    const title = (job.normalisedTitle || job.title || '').trim();
    const logoKey = job.companyId ?? companyId;
    const city =
      Array.isArray(job.location) && job.location.length > 0
        ? String(job.location[0] ?? '').split(',')[0]?.trim() ?? ''
        : '';

    return [
      <li key={oid}>
        <a
          href={jobHref}
          onClick={() =>
            trackJobClick({
              jobId: oid,
              jobTitle: title,
              companyId: job.companyId ?? companyId,
              source: 'company_profile',
              locale: jobLinkLocale,
            })
          }
          className="group flex items-center gap-2.5 rounded-xl border border-jc-border bg-jc-page/50 px-2.5 py-2 transition-colors hover:border-jc-accent/35 hover:bg-jc-accent-muted/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-jc-surface"
        >
          <div className="rounded-xl bg-transparent p-px dark:bg-[#F8F5F0] dark:ring-1 dark:ring-[#98CFA8]/35 flex-shrink-0">
            <CompanyLogo
              companyId={logoKey}
              size={28}
              variant="rounded"
              className="border-0 shadow-none dark:!border-0 dark:!bg-transparent"
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-tight text-jc-ink transition-colors group-hover:text-jc-cta dark:group-hover:text-jc-accent">
              {title}
            </p>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] text-jc-muted">
              {city ? <span className="truncate">{city}</span> : null}
              {city && job.normalisedRoleLevel ? <span aria-hidden>·</span> : null}
              {job.normalisedRoleLevel ? <span className="truncate">{job.normalisedRoleLevel}</span> : null}
              {(city || job.normalisedRoleLevel) && job.remoteness ? <span aria-hidden>·</span> : null}
              {job.remoteness ? <span className="truncate">{job.remoteness}</span> : null}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1.5">
            {salary ? (
              <span className="whitespace-nowrap rounded-md border border-jc-gold-line/20 bg-jc-gold-bg/60 px-1.5 py-0.5 text-[11px] font-medium tabular-nums leading-none text-jc-gold">
                {salary}
              </span>
            ) : null}
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full border border-jc-border bg-jc-elevated text-jc-accent transition-colors group-hover:border-jc-accent/25 group-hover:bg-jc-accent-muted"
              aria-hidden
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </a>
      </li>,
    ];
  });

  return (
    <section className="mt-0" aria-labelledby="company-open-roles-heading">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2
            id="company-open-roles-heading"
            className="text-lg font-semibold tracking-tight text-jc-ink"
          >
            {copy.openRolesHeading}
          </h2>
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-jc-accent/20 bg-jc-accent-muted dark:border-jc-accent/35 dark:bg-[#1A2E22]"
            aria-hidden
          >
            <Briefcase className="h-4 w-4 text-jc-cta dark:text-jc-accent" strokeWidth={2} />
          </div>
        </div>
        <a
          href={seeAllHref}
          className="inline-flex items-center rounded-md border border-jc-accent/30 bg-jc-accent-muted/45 px-2 py-1 text-[11px] font-medium text-jc-cta transition-colors hover:bg-jc-accent-muted/70 dark:border-jc-accent/45 dark:bg-[#1A2E22]/90 dark:text-jc-accent dark:hover:bg-[#1A2E22]"
        >
          {copy.seeAllJobs}
        </a>
      </div>
      <p className="mb-3 text-[11px] leading-snug text-jc-muted">{copy.openRolesSub}</p>

      {listItems.length === 0 ? (
        <p className="text-[13px] text-jc-muted">{copy.noOpenRoles}</p>
      ) : (
        <ul className="space-y-1.5">{listItems}</ul>
      )}
    </section>
  );
}

/** Alias for older call sites / merges — same component as `CompanyOpenRolesPanel`. */
const LiveJobList = CompanyOpenRolesPanel;

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CompanyPage() {
  const { data, isAwaitingData, isNotFound, notFoundReason } = useCompanyProfilePageData();
  const pageContext = usePageContext();

  const viewedCompanyId = data?.company?.objectID;
  useEffect(() => {
    if (!viewedCompanyId || !data) return;
    trackEvent({
      name: 'company_view',
      params: {
        company_id: viewedCompanyId,
        company_name:
          toSafeIntlStringValue(data.company.companyName, data.locale).trim() || undefined,
        industry: data.industryLabel ?? undefined,
        locale: data.locale,
      },
    });
    // Fire once per company profile, not on every data refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedCompanyId]);

  if (isNotFound) {
    const loc = (pageContext?.locale as string) === 'fi' ? 'fi' : (pageContext?.locale as string) === 'sv' ? 'sv' : 'en';
    const employersHref = `/${loc}/${LOCALIZED_PATHS[loc].bestCompanies}`;
    const copy = getCompanyProfileUiCopy(loc);
    const notFoundTitle =
      notFoundReason === 'no_algolia_profile'
        ? copy.notFoundProfilePendingTitle
        : notFoundReason === 'no_companies_row'
          ? copy.notFoundOutsideCatalogTitle
          : copy.notFoundTitle;
    const notFoundDescription =
      notFoundReason === 'no_algolia_profile'
        ? copy.notFoundProfilePendingDescription
        : notFoundReason === 'no_companies_row'
          ? copy.notFoundOutsideCatalogDescription
          : copy.notFoundDescription;
    return (
      <div className={`${shell.page} max-w-5xl mx-auto px-4 py-6 sm:py-8`}>
        <BreadcrumbNavigation
          items={[
            { label: copy.breadcrumbCompanies, href: employersHref },
            { label: '—' },
          ]}
          className="mb-4 px-0 sm:px-0"
        />
        <div className={`${shell.card} max-w-xl`}>
          <h1 className={`${shell.h1} mb-2`}>
            {notFoundTitle}
          </h1>
          <p className={shell.body}>
            {notFoundDescription}
          </p>
          <a href={employersHref} className={`${shell.link} mt-4 inline-block font-medium`}>
            {copy.breadcrumbCompanies}
          </a>
        </div>
      </div>
    );
  }

  if (isAwaitingData) {
    return (
      <div className={`${shell.page} max-w-5xl mx-auto px-4 py-6 sm:py-8`} aria-busy="true">
        <div className="mb-4 h-4 w-48 animate-pulse rounded-md bg-[#DDD5C8] dark:bg-[#4A3520]" />
        <div className={`${shell.cardHero} mb-5 sm:mb-6`}>
          <div className="flex items-start gap-4 sm:gap-5">
            <div className="h-20 w-20 shrink-0 animate-pulse rounded-2xl bg-[#F2EADF] dark:bg-[#2A1D0F]" />
            <div className="min-w-0 flex-1 space-y-3">
              <div className="h-8 max-w-md animate-pulse rounded-md bg-[#F2EADF] dark:bg-[#2A1D0F]" />
              <div className="h-4 w-3/4 max-w-lg animate-pulse rounded-md bg-[#DDD5C8] dark:bg-[#4A3520]" />
              <div className="h-4 w-2/3 max-w-md animate-pulse rounded-md bg-[#DDD5C8]/80 dark:bg-[#4A3520]/80" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="h-40 animate-pulse rounded-2xl bg-[#DDD5C8]/60 dark:bg-[#4A3520]/60" />
            <div className="h-40 animate-pulse rounded-2xl bg-[#DDD5C8]/60 dark:bg-[#4A3520]/60" />
          </div>
          <div className="h-64 animate-pulse rounded-2xl bg-[#DDD5C8]/60 dark:bg-[#4A3520]/60" />
        </div>
      </div>
    );
  }

  if (!data) {
    if (typeof window !== 'undefined') {
      console.warn('[company-profile-nav] blank render: no data, not loading, not not-found', {
        pathname: window.location.pathname,
        routeCompanyslug: (pageContext?.routeParams as { companyslug?: string } | undefined)?.companyslug ?? null,
        resolvedSlug: resolveCompanyProfileRouteSlug(pageContext),
        hint: 'Enable verbose logs: localStorage.setItem("debug_company_profile_nav","1")',
      });
    }
    return null;
  }
  const { company, locale, industryLabel } = data;
  const loc: CompanyProfileLocale = locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en';
  const copy = getCompanyProfileUiCopy(loc);
  const companyDisplayName =
    toSafeIntlStringValue(company.companyName, locale).trim() || company.objectID;
  const trendInfo = hiringTrendLabel(company.hiringTrend, copy);
  const narrative = buildCompanySeoNarrative(company, companyDisplayName, loc, industryLabel ?? '');
  const seoTables = buildCompanyProfileDataTables(company, copy);
  const companyAboutForPage = filterCompanyAboutForPageLocale(company.about, locale);

  const rawRegistryBusinessLine = resolveRegistryMainBusinessLineForPageLocale(company, locale);
  const registryBusinessLine =
    loc === 'fi'
      ? rawRegistryBusinessLine
      : filterCompanyAboutForPageLocale(rawRegistryBusinessLine, locale);
  const seeAllJobsHref = generateUrlFromIndexUiState(
    { refinementList: { companyId: [company.objectID] }, page: 0 },
    getIndexName(locale),
    jobsListingPathForLocale(locale),
    { forceJobsListingForCompany: true },
  );

  return (
    <div className={`${shell.page} max-w-5xl mx-auto px-4 py-6 sm:py-8`}>
      <BreadcrumbNavigation
        items={[
          { label: copy.breadcrumbCompanies, href: `/${loc}/${LOCALIZED_PATHS[loc].bestCompanies}` },
          { label: companyDisplayName },
        ]}
        className="mb-4 px-0 sm:px-0"
      />

      {/* Hero */}
      <div className={`${shell.cardHero} mb-5 sm:mb-6`}>
        <div className="flex items-start gap-4 sm:gap-5">
          <div className="shrink-0">
            <CompanyLogo
              companyName={company.objectID}
              initialsSourceName={companyDisplayName}
              size={80}
              variant="default"
              isTyomarkkinatori={false}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className={shell.h1}>
                {companyDisplayName}
              </h1>
              {trendInfo && (
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${trendInfo.colour}`}>
                  {trendInfo.label}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
              {industryLabel ? <span>{industryLabel}</span> : null}
              {company.registryCity && (
                <span>· {company.registryCity}{company.registryCountry ? `, ${company.registryCountry}` : ''}</span>
              )}
              {(company.companyHeadCountMin || company.companyHeadCountMax) && (
                <span>
                  ·&nbsp;
                  {company.companyHeadCountMin && company.companyHeadCountMax
                    ? `${company.companyHeadCountMin}–${company.companyHeadCountMax} ${copy.employees}`
                    : `${company.companyHeadCountMin ?? company.companyHeadCountMax} ${copy.employees}`}
                </span>
              )}
              {company.companyAgeYears != null && (
                <span>
                  · {copy.foundedYearEst}{' '}
                  {new Date().getFullYear() - Math.round(company.companyAgeYears)}
                  {copy.foundedYearSuffix}
                </span>
              )}
              {company.companyURL && (
                <a
                  href={company.companyURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${shell.link} font-medium underline-offset-2 hover:underline`}
                >
                  {copy.website}
                </a>
              )}
            </div>

            {companyAboutForPage ? (
              <p className={`mt-3 line-clamp-4 leading-relaxed ${shell.body}`}>
                {companyAboutForPage}
              </p>
            ) : null}

            <SocialLinks company={company} />
          </div>
        </div>

        {/* Employer overview — same card as hero */}
        <div
          className="mt-5 border-t border-[#DDD5C8] pt-5 dark:border-[#4A3520]"
          aria-labelledby="company-seo-summary-heading"
        >
          <h2 id="company-seo-summary-heading" className={shell.h2}>
            {copy.summaryHeading}
          </h2>
          <p className={`mt-2 leading-relaxed ${shell.body}`}>{narrative.lead}</p>
          {narrative.detail ? (
            <p className={`mt-2 leading-relaxed ${shell.body}`}>{narrative.detail}</p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        {/* Left: all graphs & company data — single column; right: open roles (lg) */}
        <div className="order-2 min-w-0 space-y-4 sm:space-y-5 lg:order-1">
          <p className={`${shell.muted} max-w-3xl leading-relaxed`}>{copy.chartSectionIntro}</p>

          {/* All analytics in one column; open roles live in the aside */}
          <div className="space-y-4 sm:space-y-5">
            <EmployerScoreCard company={company} copy={copy} />
            <CompanyCommunityInsights company={company} />
            <SalaryBand company={company} copy={copy} />

            {company.remotenessDistribution && (
              <div className={shell.card}>
                <StarburstChart
                  data={{
                    Remote: company.remotenessDistribution.remote,
                    Hybrid: company.remotenessDistribution.hybrid,
                    Onsite: company.remotenessDistribution.onsite,
                  }}
                  title={copy.workModelChartTitle}
                  titleKey="remotenessDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </div>
            )}

            {(company.revenue || company.profit || company.profitMargin) && (
              <div className={shell.card}>
                <MoneyGraph
                  revenue={company.revenue}
                  profit={company.profit}
                  profitMargin={company.profitMargin}
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </div>
            )}

            {company.companyMonthlyCounts && Object.keys(company.companyMonthlyCounts).length >= 2 && (
              <div className={shell.card}>
                <MonthlyHiringChart
                  monthlyCounts={company.companyMonthlyCounts}
                  headcountMin={company.companyHeadCountMin}
                  headcountMax={company.companyHeadCountMax}
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </div>
            )}

            {company.roleDistribution && Object.keys(company.roleDistribution).length > 0 && (
              <div className={shell.card}>
                <StarburstChart
                  data={company.roleDistribution}
                  title={copy.rolesChartTitle}
                  titleKey="roleDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={8}
                  density="compact"
                />
              </div>
            )}

            {company.locationDistribution && Object.keys(company.locationDistribution).length > 0 && (
              <div className={shell.card}>
                <StarburstChart
                  data={company.locationDistribution}
                  title={copy.locationsChartTitle}
                  titleKey="locationDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={6}
                  density="compact"
                />
              </div>
            )}

            {company.registryBusinessId && (
              <div className={shell.card}>
                <h2 className={`${shell.h2} mb-3`}>
                  {copy.companyInfoHeading}
                </h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {company.registryBusinessId && (
                    <>
                      <dt className="text-[#7A6E5F] dark:text-[#B8A48C]">{copy.businessId}</dt>
                      <dd className="font-medium text-[#1E1A14] dark:text-[#F2EADF]">{company.registryBusinessId}</dd>
                    </>
                  )}
                  {company.registryStatus && (
                    <>
                      <dt className="text-[#7A6E5F] dark:text-[#B8A48C]">{copy.status}</dt>
                      <dd className="font-medium text-[#1E1A14] dark:text-[#F2EADF]">{company.registryStatus}</dd>
                    </>
                  )}
                  {company.registryRegistrationDate && (
                    <>
                      <dt className="text-[#7A6E5F] dark:text-[#B8A48C]">{copy.founded}</dt>
                      <dd className="font-medium text-[#1E1A14] dark:text-[#F2EADF]">
                        {company.registryRegistrationDate.substring(0, 10)}
                      </dd>
                    </>
                  )}
                  {registryBusinessLine && (
                    <>
                      <dt className="text-[#7A6E5F] dark:text-[#B8A48C]">{copy.businessLine}</dt>
                      <dd className="col-span-1 font-medium text-[#1E1A14] dark:text-[#F2EADF]">
                        {registryBusinessLine}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* Open roles — right on large screens; first below hero on small screens */}
        <aside className="order-1 min-w-0 lg:order-2 lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pl-1">
          <LiveJobList
            companyId={company.objectID}
            locale={locale}
            seeAllHref={seeAllJobsHref}
            copy={copy}
          />
        </aside>
      </div>

      <div className="max-w-6xl mx-auto px-2 sm:px-6 pb-6">
        <EngagementStrip
          feedbackSource="company_profile"
          variant="card"
          feedbackMetadata={{
            companyId: company.objectID,
            companyName: companyDisplayName,
            locale,
          }}
        />
      </div>

      <CompanyProfileSeoTables tables={seoTables} visuallyHiddenHeading={copy.dataTablesHeading} />
    </div>
  );
}
